<template>
  <div class="ai-chart">
    <div ref="chartEl" class="ai-chart-canvas"></div>
    <div v-if="chart.band" class="ai-chart-legend">
      <span><i class="sw line"></i>{{ seriesName }}</span>
      <span v-if="chart.band"><i class="sw band"></i>最小~最大</span>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'

/**
 * 智能体图表组件
 * 兼容后端 tool_end 事件携带的 chart 载荷：
 * {
 *   title: string,            // 标题
 *   x: string[],              // X 轴标签（如 ["08:00", "09:00"]）
 *   yUnit: string,            // Y 轴单位（如 "W/m²"）
 *   series: [{ name, data }], // 折线数据，data 中 null 表示缺值断线
 *   band: { low: [], high: [] } // 可选：最小~最大区间带
 * }
 */
export default {
  name: 'AiChart',
  props: {
    chart: { type: Object, required: true },
    // 主题：dark（大屏）/ light（后台）
    theme: { type: String, default: 'light' }
  },
  data() {
    return {
      chartInstance: null,
      ro: null
    }
  },
  computed: {
    seriesName() {
      const s = this.chart.series && this.chart.series[0]
      return (s && s.name) || '数值'
    }
  },
  watch: {
    chart: {
      deep: true,
      handler() {
        this.render()
      }
    }
  },
  mounted() {
    this.chartInstance = echarts.init(this.$refs.chartEl, this.theme === 'dark' ? 'dark' : undefined)
    this.render()
    // 容器宽度变化时自适应；用 rAF 防抖，避免 resize 触发布局变化引发
    // "ResizeObserver loop" 循环告警
    if (typeof ResizeObserver !== 'undefined') {
      this.ro = new ResizeObserver(() => {
        if (!this.chartInstance) return
        if (this._raf) cancelAnimationFrame(this._raf)
        this._raf = requestAnimationFrame(() => {
          this._raf = null
          this.chartInstance.resize()
        })
      })
      this.ro.observe(this.$refs.chartEl)
    }
  },
  beforeDestroy() {
    if (this._raf) cancelAnimationFrame(this._raf)
    if (this.ro) this.ro.disconnect()
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
  },
  methods: {
    render() {
      if (!this.chartInstance || !this.chart) return
      const c = this.chart
      const series = (c.series && c.series[0]) || { name: '', data: [] }
      const option = {
        backgroundColor: 'transparent',
        title: {
          text: c.title || '',
          left: 4,
          top: 2,
          textStyle: {
            fontSize: 13,
            fontWeight: 600,
            color: this.theme === 'dark' ? '#cfeaff' : '#303133'
          }
        },
        tooltip: {
          trigger: 'axis',
          valueFormatter: (v) => (v == null ? '—' : v + (c.yUnit ? ' ' + c.yUnit : ''))
        },
        grid: { left: 48, right: 18, top: 34, bottom: 30 },
        xAxis: {
          type: 'category',
          data: c.x || [],
          boundaryGap: false,
          axisLine: { lineStyle: { color: this.theme === 'dark' ? 'rgba(0,220,255,0.25)' : '#dcdfe6' } },
          axisLabel: {
            color: this.theme === 'dark' ? '#8fb3c8' : '#909399',
            fontSize: 11
          }
        },
        yAxis: {
          type: 'value',
          name: c.yUnit || '',
          nameTextStyle: { fontSize: 11, color: this.theme === 'dark' ? '#8fb3c8' : '#909399' },
          splitLine: {
            lineStyle: { color: this.theme === 'dark' ? 'rgba(0,220,255,0.12)' : '#ebeef5' }
          },
          axisLabel: { fontSize: 11, color: this.theme === 'dark' ? '#8fb3c8' : '#909399' }
        },
        series: []
      }

      // 主折线
      option.series.push({
        name: series.name || '数值',
        type: 'line',
        data: series.data || [],
        connectNulls: false, // null 断线
        smooth: true,
        symbol: 'circle',
        symbolSize: 4,
        lineStyle: { width: 2, color: '#00dcff' },
        itemStyle: { color: '#00dcff' }
      })

      // 区间带：low 入栈（透明），high-low 填充，形成上下界之间的色带
      if (c.band && Array.isArray(c.band.low) && Array.isArray(c.band.high)) {
        const n = (c.x || []).length
        const low = c.band.low.slice(0, n)
        const high = c.band.high.slice(0, n)
        option.series.push({
          name: '最小',
          type: 'line',
          data: low,
          stack: 'band',
          lineStyle: { opacity: 0 },
          itemStyle: { opacity: 0 },
          areaStyle: { opacity: 0 },
          silent: true,
          tooltip: { show: false }
        })
        option.series.push({
          name: '区间',
          type: 'line',
          data: high.map((h, i) => (h != null && low[i] != null ? h - low[i] : null)),
          stack: 'band',
          lineStyle: { opacity: 0 },
          itemStyle: { opacity: 0 },
          areaStyle: {
            color: this.theme === 'dark' ? 'rgba(0,220,255,0.16)' : 'rgba(64,158,255,0.14)'
          },
          silent: true,
          tooltip: { show: false }
        })
      }

      this.chartInstance.setOption(option, true)
    }
  }
}
</script>

<style scoped>
.ai-chart {
  width: 100%;
  margin-top: 10px;
}
.ai-chart-canvas {
  width: 100%;
  height: 220px;
}
.ai-chart-legend {
  display: flex;
  gap: 16px;
  padding-left: 4px;
  font-size: 11px;
}
.ai-chart-legend span {
  display: flex;
  align-items: center;
  gap: 5px;
}
.ai-chart-legend .sw {
  width: 12px;
  height: 8px;
  border-radius: 2px;
}
.ai-chart-legend .sw.line {
  background: #00dcff;
}
.ai-chart-legend .sw.band {
  background: rgba(0, 220, 255, 0.25);
  border: 1px solid rgba(0, 220, 255, 0.5);
}
</style>
